import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { type RefObject, useMemo, useRef } from 'react';
import seedrandom from 'seedrandom';
import * as THREE from 'three';
import {
  axialHexDistance,
  axialNeighbors,
  axialToWorld,
  createCircularHexMapShape,
  createPerlinIslandsNoise,
  createSingleLargeIslandPreset,
  generateHexMap,
  getHexTileMapKey,
  HexMap,
  type HexTileCoordinates,
  type HexTileMap,
  type HexTileMapKey,
  hexMapConfig,
} from '@/game/hexmap';
import { baseRuleSet } from '@/game/rules';
import type { Building, SimState, TileVisibility } from '@/game/sim';
import { PLAYER_COLORS } from '@/game/view/palette';
import { BuildingModel, glowMaterial, UnitModel } from '../game/models';
import { OceanSurround } from '../game/OceanSurround';
import { TileDecorations } from '../game/TileDecorations';

/**
 * Title-screen backdrop: a freshly generated island (new seed every load),
 * dressed with a few settlements, filmed by a cinematic camera that cuts to a
 * new shot every {@link SHOT_SECONDS} — gliding through forest canopy, drifting
 * along the coast, circling a city, or sweeping high over the island. Cuts fade
 * through black via the `fadeRef` overlay.
 */

const MAP_RADIUS = 22;
const OCEAN_RADIUS = 56;
const SHOT_SECONDS = 10;
const FADE_SECONDS = 1.1;
/** Afternoon sun; the directional light shines from the same place. */
const SUN: [number, number, number] = [-50, 30, 25];
/** Distant haze; the sky's horizon colour, so land and ocean melt into it. */
const HAZE = '#cddceb';
const ZENITH = '#2f6fc0';
const SKY_RADIUS = 400;
const GROUND = hexMapConfig.hexMap.tile.thickness;
const TILE_RADIUS = hexMapConfig.hexMap.tile.radius;
const ORIGIN = { q: 0, r: 0 };
const UNIT_TYPES = ['worker', 'warrior', 'scout', 'worker', 'settler'];

const visible = (): TileVisibility => 'visible';

type ShotKind = 'canopy' | 'coast' | 'settlement' | 'vista';

interface Shot {
  /** Camera position and look-at target at time t (seconds into the shot). */
  at: (t: number, pos: THREE.Vector3, look: THREE.Vector3) => void;
}

interface Dressing {
  cities: Building[];
  units: Array<{ id: string; type: string; pos: HexTileCoordinates; color: number; yaw: number }>;
}

function world(c: HexTileCoordinates): THREE.Vector3 {
  const w = axialToWorld(c, TILE_RADIUS);
  return new THREE.Vector3(w.x, GROUND, w.z);
}

function tilesOf(tiles: HexTileMap, types: string[], maxRadius: number): HexTileCoordinates[] {
  const out: HexTileCoordinates[] = [];
  for (const tile of tiles.values()) {
    if (types.includes(tile.type) && axialHexDistance(ORIGIN, tile.coordinates) <= maxRadius) {
      out.push(tile.coordinates);
    }
  }
  return out;
}

/** Up to three cities on open land, spread apart, each with a few units around it. */
function dressMap(tiles: HexTileMap, rng: () => number): Dressing {
  const land = tilesOf(tiles, ['default', 'field'], MAP_RADIUS - 6);
  const cities: Building[] = [];
  for (let attempt = 0; attempt < 200 && cities.length < 3; attempt++) {
    const pos = land[Math.floor(rng() * land.length)];
    if (!pos || cities.some((c) => axialHexDistance(c.pos, pos) < 9)) continue;
    cities.push({
      id: `city-${cities.length}`,
      type: 'city',
      owner: `p${cities.length}`,
      pos,
      hp: 1,
      buildProgress: 1,
    });
  }
  // No forest: units there would stand inside the trees.
  const open = new Set(['default', 'field', 'beach']);
  const units: Dressing['units'] = [];
  cities.forEach((city, color) => {
    for (const n of axialNeighbors(city.pos)) {
      const tile = tiles.get(getHexTileMapKey(n));
      if (!tile || !open.has(tile.type) || rng() < 0.4) continue;
      units.push({
        id: `u${units.length}`,
        type: UNIT_TYPES[Math.floor(rng() * UNIT_TYPES.length)] ?? 'worker',
        pos: n,
        color,
        yaw: rng() * Math.PI * 2,
      });
    }
  });
  return { cities, units };
}

function makeShot(kind: ShotKind, tiles: HexTileMap, dressing: Dressing, rng: () => number): Shot {
  const pick = <T,>(xs: T[]): T | undefined => xs[Math.floor(rng() * xs.length)];
  const angle = rng() * Math.PI * 2;
  const dir = new THREE.Vector3(Math.cos(angle), 0, Math.sin(angle));

  if (kind === 'canopy') {
    // Just above the forest floor, gliding between the trunks.
    const focus = pick(tilesOf(tiles, ['forest'], MAP_RADIUS - 5));
    if (focus) {
      const start = world(focus).addScaledVector(dir, -2.2);
      const height = 0.32 + rng() * 0.2;
      return {
        at(t, pos, look) {
          pos.copy(start).addScaledVector(dir, t * 0.32);
          pos.y = GROUND + height;
          look.copy(pos).addScaledVector(dir, 6);
          look.y = GROUND + height * 0.55;
        },
      };
    }
  }

  if (kind === 'coast') {
    // Low over the sand, drifting sideways and looking inland.
    const beach = pick(tilesOf(tiles, ['beach'], MAP_RADIUS));
    if (beach) {
      const b = world(beach);
      const inland = new THREE.Vector3(-b.x, 0, -b.z).normalize();
      const side = new THREE.Vector3(-inland.z, 0, inland.x);
      const start = b.clone().addScaledVector(inland, -2.5);
      return {
        at(t, pos, look) {
          pos.copy(start).addScaledVector(side, (t - SHOT_SECONDS / 2) * 0.3);
          pos.y = GROUND + 1.1;
          look.copy(pos).addScaledVector(inland, 9);
          look.y = GROUND;
        },
      };
    }
  }

  if (kind === 'settlement') {
    const city = pick(dressing.cities);
    if (city) {
      const centre = world(city.pos);
      const spin = rng() < 0.5 ? -1 : 1;
      return {
        at(t, pos, look) {
          const a = angle + spin * t * 0.07;
          pos.set(centre.x + Math.cos(a) * 3.4, GROUND + 1.3, centre.z + Math.sin(a) * 3.4);
          look.copy(centre);
          look.y = GROUND + 0.35;
        },
      };
    }
  }

  // Vista: high sweep across the island toward its far side.
  const start = dir.clone().multiplyScalar(-MAP_RADIUS * 1.25);
  return {
    at(t, pos, look) {
      pos.copy(start).addScaledVector(dir, t * 0.9);
      pos.y = 9 - t * 0.2;
      look.copy(pos).addScaledVector(dir, 16);
      look.y = 0;
    },
  };
}

const SHOT_ORDER: ShotKind[] = ['canopy', 'coast', 'settlement', 'vista'];

function CinematicCamera({
  tiles,
  dressing,
  seed,
  fadeRef,
}: {
  tiles: HexTileMap;
  dressing: Dressing;
  seed: string;
  fadeRef: RefObject<HTMLDivElement | null>;
}) {
  const camera = useThree((s) => s.camera);
  const rng = useMemo(() => seedrandom(`shots:${seed}`), [seed]);
  const shot = useRef<{ index: number; shot: Shot } | null>(null);
  const pos = useRef(new THREE.Vector3());
  const look = useRef(new THREE.Vector3());

  useFrame(({ clock }) => {
    const elapsed = clock.getElapsedTime();
    const index = Math.floor(elapsed / SHOT_SECONDS);
    if (shot.current?.index !== index) {
      // Always open on the canopy, then cycle through the rest in order.
      const kind = SHOT_ORDER[index % SHOT_ORDER.length] ?? 'vista';
      shot.current = { index, shot: makeShot(kind, tiles, dressing, rng) };
    }
    const t = elapsed - index * SHOT_SECONDS;
    shot.current.shot.at(t, pos.current, look.current);
    camera.position.copy(pos.current);
    camera.lookAt(look.current);

    const edge = Math.min(t, SHOT_SECONDS - t);
    // The very first shot fades in from black too.
    const opacity = 1 - THREE.MathUtils.smoothstep(edge, 0, FADE_SECONDS);
    if (fadeRef.current) fadeRef.current.style.opacity = opacity.toFixed(3);
  });

  return null;
}

const skyMaterial = new THREE.ShaderMaterial({
  side: THREE.BackSide,
  depthWrite: false,
  toneMapped: false,
  uniforms: {
    horizon: { value: new THREE.Color(HAZE) },
    zenith: { value: new THREE.Color(ZENITH) },
    sunDir: { value: new THREE.Vector3(...SUN).normalize() },
  },
  vertexShader: /* glsl */ `
    varying vec3 vDir;
    void main() {
      vDir = normalize(position);
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    uniform vec3 horizon;
    uniform vec3 zenith;
    uniform vec3 sunDir;
    varying vec3 vDir;
    void main() {
      vec3 dir = normalize(vDir);
      float up = pow(smoothstep(0.0, 0.75, dir.y), 0.55);
      vec3 color = mix(horizon, zenith, up);
      float sun = max(dot(dir, sunDir), 0.0);
      color += vec3(1.0, 0.93, 0.8) * (pow(sun, 900.0) * 1.2 + pow(sun, 24.0) * 0.28);
      gl_FragColor = vec4(color, 1.0);
      #include <colorspace_fragment>
    }
  `,
});

/**
 * Gradient sky dome with a sun: haze at the horizon to deep blue overhead.
 * Follows the camera so the horizon never shifts as shots move.
 */
function GradientSky() {
  const ref = useRef<THREE.Mesh>(null);
  useFrame(({ camera }) => ref.current?.position.copy(camera.position));
  return (
    <mesh ref={ref} material={skyMaterial} renderOrder={-1} frustumCulled={false}>
      <sphereGeometry args={[SKY_RADIUS, 32, 16]} />
    </mesh>
  );
}

function Dressings({ dressing }: { dressing: Dressing }) {
  const accents = useMemo(
    () =>
      PLAYER_COLORS.map((color) => new THREE.MeshStandardMaterial({ color, flatShading: true })),
    []
  );
  const glows = useMemo(() => PLAYER_COLORS.map((color) => glowMaterial(color)), []);
  return (
    <group>
      {dressing.cities.map((city, i) => {
        const w = world(city.pos);
        return (
          <group key={city.id} position={w}>
            <BuildingModel
              type="city"
              accent={accents[i] ?? accents[0]}
              glow={glows[i] ?? glows[0]}
              level={1}
            />
          </group>
        );
      })}
      {dressing.units.map((unit) => {
        const w = world(unit.pos);
        return (
          <group key={unit.id} position={w} rotation={[0, unit.yaw, 0]}>
            <UnitModel
              type={unit.type}
              accent={accents[unit.color] ?? accents[0]}
              glow={glows[unit.color] ?? glows[0]}
              level={1}
            />
          </group>
        );
      })}
    </group>
  );
}

export function SplashScene({ fadeRef }: { fadeRef: RefObject<HTMLDivElement | null> }) {
  // A new island every time the title screen loads.
  const seed = useMemo(() => Math.random().toString(36).slice(2), []);
  const tiles = useMemo(
    () =>
      generateHexMap({
        shape: createCircularHexMapShape(MAP_RADIUS),
        noise: createPerlinIslandsNoise(createSingleLargeIslandPreset(MAP_RADIUS)),
        seed,
      }).tiles,
    [seed]
  );
  const dressing = useMemo(() => dressMap(tiles, seedrandom(`dress:${seed}`)), [tiles, seed]);

  // TileDecorations reads trees/wheat from sim state; a static stand-in with
  // untouched tiles and the dressing's cities (which clear the props under them).
  const decor = useMemo(() => {
    const byKey = new Map<HexTileMapKey, Building>(
      dressing.cities.map((c) => [getHexTileMapKey(c.pos), c])
    );
    const state = {
      buildings: Object.fromEntries(dressing.cities.map((c) => [c.id, c])),
      tileResourceOverride: {},
    } as unknown as SimState;
    return {
      state,
      tileView: (key: HexTileMapKey) => ({ remaining: null, building: byKey.get(key) ?? null }),
    };
  }, [dressing]);

  return (
    <Canvas camera={{ fov: 50, near: 0.03, far: 500 }} dpr={[1, 2]}>
      <color attach="background" args={[HAZE]} />
      <GradientSky />
      <fog attach="fog" args={[HAZE, 12, 70]} />
      <ambientLight intensity={0.35} color="#dce7f2" />
      <hemisphereLight args={['#cfe2ff', '#4a3a26', 0.9]} />
      <directionalLight position={SUN} intensity={2} color="#ffe2b8" />
      <HexMap tiles={tiles} getTileVisibility={visible} />
      <OceanSurround
        mapRadius={MAP_RADIUS}
        oceanRadius={OCEAN_RADIUS}
        backgroundColor={HAZE}
        getTileVisibility={visible}
      />
      <TileDecorations
        tiles={tiles}
        tileYield={baseRuleSet.pacing.tileYield}
        state={decor.state}
        getTileVisibility={visible}
        tileView={decor.tileView}
      />
      <Dressings dressing={dressing} />
      <CinematicCamera tiles={tiles} dressing={dressing} seed={seed} fadeRef={fadeRef} />
    </Canvas>
  );
}
